'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TopbarProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

function getUTCStrings() {
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' });
  const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return { timeStr, dateStr };
}

export default function Topbar({ title, subtitle, actions }: TopbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [clock, setClock] = useState<{ timeStr: string; dateStr: string } | null>(null);

  useEffect(() => {
    setClock(getUTCStrings());
    const interval = setInterval(() => {
      setClock(getUTCStrings());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="h-14 bg-zinc-950/80 backdrop-blur-sm border-b border-zinc-800/60 flex items-center px-6 lg:px-8 flex-shrink-0 sticky top-0 z-30">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-sm font-semibold text-zinc-100 leading-none">{title}</h1>
            {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 ml-4">
        {/* UTC clock */}
        <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-900 border border-zinc-800 mr-2">
          <Icon name="ClockIcon" size={12} className="text-zinc-500" />
          <span className="font-mono text-xs text-zinc-400 tabular-nums">
            {clock ? clock.timeStr : '--:--:--'}
          </span>
          <span className="text-zinc-700">·</span>
          <span className="font-mono text-xs text-zinc-600 tabular-nums">UTC</span>
        </div>

        {actions && <div className="flex items-center gap-2">{actions}</div>}

        {/* Search */}
        <button
          onClick={() => setSearchOpen(!searchOpen)}
          className="p-2 rounded-md hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label="Search"
        >
          <Icon name="MagnifyingGlassIcon" size={15} />
        </button>

        {/* Notifications */}
        <button className="relative p-2 rounded-md hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors" aria-label="Notifications">
          <Icon name="BellIcon" size={15} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-rose-500 rounded-full"></span>
        </button>

        {/* Settings */}
        <button className="p-2 rounded-md hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors" aria-label="Settings">
          <Icon name="Cog6ToothIcon" size={15} />
        </button>
      </div>
    </header>
  );
}